import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { GridApi, GridOptions, GridReadyEvent, IRowNode, SelectionChangedEvent } from '@ag-grid-community/core';

import useApiClient from '../../../../../../Shared/services/api';
import { getColumnDefs, getColumnVisibilityConfig, getColumnManagerPresets } from '../config/columnDefinitions';
import { FILTERABLE_COLUMNS } from '../constants/filterableColumns';
import { exportBookingsToExcel, NameLookupMaps } from '../utils/csvUtils';
import { getFilterGroups } from '../config/filterConfig';
import { getGridOptions } from '../config/gridOptions';
import { ApiError } from '../../../../../../Shared/interfaces/ApiError';
import {
  getCreativesOptions,
  getBrandConfirmationOptions,
  getRoOptions,
} from '../../../../CreateBooking/config/dropDownConfig';
import { ActionIconConfig } from '../../../config/actionButtonConfig';
import { QueueSheetManagementRows } from '../../../interface/QueueSheetInterface';
import { GTM_STATUS_ENUM_TO_LABEL_MAP } from '../../../../CreateBooking/constants/DetailedBookingConstants';
import { FORM_FIELDS, BOOKING_FIELD_COLUMN_MAP } from '../../../../CreateBooking/constants/FormFieldsConstants';
import { getFormattedDate } from '../../../../../../Shared/utils/helpers';
import {
  getAccountExecutives,
  getAccountManagers,
} from '../../../../../services/service';
import { EndpointQueryKeys } from '../../../../../constants/endpoints';
import {
  AccountExecutivesResponse,
  AccountManagersResponse,
  AEUser,
  AMUser,
} from '../../../../CreateBooking/interface/ApiInterfaces';

interface UseBookingTableParams {
  bookingData: QueueSheetManagementRows[];
  actionButtonConfig: ActionIconConfig[];
  brands: string[];
  slotPackages: string[];
  artEventId: string;
  // When true, the grid shows selection checkboxes and tracks selected rows for bulk actions.
  enableSelection?: boolean;
}

export const useBookingTable = ({
  bookingData,
  actionButtonConfig,
  brands,
  slotPackages,
  artEventId,
  enableSelection = false,
}: UseBookingTableParams) => {
  const { t } = useTranslation();
  const api = useApiClient();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<QueueSheetManagementRows[]>([]);
  const [fieldFilter, setFieldFilter] = useState<{ field: string | null; value: string }>({ field: null, value: '' });

  // --- Data Fetching & Name Maps ---
  const { data: aeUsersResponse, isInitialLoading: isAeUsersLoading } = useQuery<
    AccountExecutivesResponse,
    ApiError
  >({
    queryKey: [EndpointQueryKeys.GET_ACCOUNT_EXECUTIVES],
    queryFn: () => getAccountExecutives(api),
    enabled: !!api,
  });

  const aeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (aeUsersResponse?.accountExecutives || []).forEach((user: AEUser) => {
      map.set(user.userId, `${user.firstName} ${user.lastName}`);
    });
    return map;
  }, [aeUsersResponse]);

  const { data: amUsersResponse, isInitialLoading: isAmUsersLoading } = useQuery<
    AccountManagersResponse,
    ApiError
  >({
    queryKey: [EndpointQueryKeys.GET_ACCOUNT_MANAGERS],
    queryFn: () => getAccountManagers(api),
    enabled: !!api,
  });

  const amNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (amUsersResponse?.accountManagers || []).forEach((user: AMUser) => {
      map.set(user.userId, `${user.firstName} ${user.lastName}`);
    });
    return map;
  }, [amUsersResponse]);

  const isNameDataLoading = isAeUsersLoading || isAmUsersLoading;

  // --- Display Value Handlers ---
  const getRoDisplayValue = useCallback((roValue: string) => {
    const option = getRoOptions(t).find((option) => option.value === roValue);
    return option ? option.label : roValue;
  }, [t]);

  const getBrandConfirmationDisplayValue = useCallback((brandConfirmationValue: string) => {
    const option = getBrandConfirmationOptions(t).find(
      (option) => option.value === brandConfirmationValue
    );
    return option ? option.label : brandConfirmationValue;
  }, [t]);

  const getCreativesDisplayValue = useCallback((creativesValue: string) => {
    const creativesOption = getCreativesOptions(t).find(
      (opt) => opt.enum === creativesValue
    );
    return creativesOption ? creativesOption.label : creativesValue;
  }, [t]);

  const fieldDisplayHandlers = useMemo(() => ({
    [FORM_FIELDS.RO]: getRoDisplayValue,
    [FORM_FIELDS.BRAND_CONFIRMATION]: getBrandConfirmationDisplayValue,
    [FORM_FIELDS.CREATIVES]: getCreativesDisplayValue,
    [FORM_FIELDS.MODIFIED_AT]: getFormattedDate,
  }), [getRoDisplayValue, getBrandConfirmationDisplayValue, getCreativesDisplayValue]);

  const getDisplayValue = useCallback((colKey: string, value: string | number) => {
    const handler = fieldDisplayHandlers[colKey as keyof typeof fieldDisplayHandlers];
    return handler ? handler(value) : value;
  }, [fieldDisplayHandlers]);

  // --- Grid State ---
  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
  }, []);

  const onFilterChanged = useCallback(() => {
    if (!gridApi) return;
    if (gridApi.getDisplayedRowCount() === 0) {
      gridApi.showNoRowsOverlay();
    } else {
      gridApi.hideOverlay();
    }
  }, [gridApi]);

  const onSelectionChanged = useCallback((event: SelectionChangedEvent<QueueSheetManagementRows>) => {
    setSelectedRows(event.api.getSelectedRows());
  }, []);

  const clearSelection = useCallback(() => {
    gridApi?.deselectAll();
    setSelectedRows([]);
  }, [gridApi]);

  useEffect(() => {
    return () => { setGridApi(null); };
  }, []);

  // --- Filtering ---
  const filteredBookingData = useMemo(() => {
    if (!fieldFilter.field || !fieldFilter.value) return bookingData;
    const columnKey = BOOKING_FIELD_COLUMN_MAP[fieldFilter.field];
    if (!columnKey) return bookingData;
    const searchTermLower = fieldFilter.value.toLowerCase();
    return bookingData.filter(row => {
      const cellValue = row[columnKey];
      return cellValue && String(cellValue).toLowerCase().includes(searchTermLower);
    });
  }, [bookingData, fieldFilter]);

  const filterGroups = useMemo(() => {
    const gtmStatusOptions = GTM_STATUS_ENUM_TO_LABEL_MAP.map(option => ({
      value: option.value,
      label: t(option.label),
    }));
    return getFilterGroups(brands, slotPackages, t, gtmStatusOptions);
  }, [brands, slotPackages, t]);

  // --- Name Lookup Maps ---
  const nameLookupMaps: NameLookupMaps = useMemo(
    () => ({ aeNameMap, amNameMap }),
    [aeNameMap, amNameMap]
  );

  // --- Export ---
  const handleExport = useCallback(() => {
    try {
      setExportError(null);
      if (!gridApi) return;

      const filterModel = gridApi.getFilterModel();
      const selectedBrands = filterModel[FILTERABLE_COLUMNS.BRAND]?.values?.map((v: { value?: string } | string) =>
        typeof v === 'string' ? v : v.value || ''
      ) || [];

      if (selectedBrands.length > 1) {
        selectedBrands.forEach((brand: string, index: number) => {
          setTimeout(() => {
            try {
              const brandRows: QueueSheetManagementRows[] = [];
              gridApi.forEachNodeAfterFilter((node: IRowNode<QueueSheetManagementRows>) => {
                if (node.data && node.data[FILTERABLE_COLUMNS.BRAND] === brand) {
                  brandRows.push(node.data);
                }
              });
              exportBookingsToExcel(brandRows, `${brand}.xlsx`, nameLookupMaps);
            } catch (error) {
              console.error('Export failed:', error);
              setExportError(t('export-failed-message'));
            }
          }, index * 100);
        });
      } else {
        const filteredRows: QueueSheetManagementRows[] = [];
        gridApi.forEachNodeAfterFilter((node: IRowNode<QueueSheetManagementRows>) => {
          if (node.data) filteredRows.push(node.data);
        });
        const filename = selectedBrands.length === 1
          ? `${selectedBrands[0]}.xlsx`
          : `${artEventId}-bookings-export.xlsx`;
        exportBookingsToExcel(filteredRows, filename, nameLookupMaps);
      }
    } catch (error) {
      console.error('Export failed:', error);
      setExportError(t('export-failed-message'));
    }
  }, [gridApi, artEventId, t, nameLookupMaps]);

  // --- Column & Grid Config ---
  const columnVisibilityConfig = useMemo(() => getColumnVisibilityConfig(), []);
  const columnManagerPresets = useMemo(() => getColumnManagerPresets(t), [t]);

  const gridOptions = useMemo<GridOptions<QueueSheetManagementRows>>(() => ({
    ...getGridOptions(t),
    ...(enableSelection
      ? {
          rowSelection: 'multiple' as const,
          // Selection is checkbox-only so a normal row click never toggles it.
          suppressRowClickSelection: true,
          onSelectionChanged,
        }
      : {}),
  }), [t, enableSelection, onSelectionChanged]);

  const columnDefs = useMemo(() => {
    return getColumnDefs({ t, actionButtonConfig, brands, getDisplayValue, enableSelection });
  }, [t, actionButtonConfig, brands, getDisplayValue, enableSelection]);

  return {
    gridProps: {
      gridApi,
      gridOptions,
      columnDefs,
      onGridReady,
      onFilterChanged,
    },
    exportProps: {
      handleExport,
      exportError,
      setExportError,
      isExportDisabled: isNameDataLoading,
    },
    filterProps: {
      filteredBookingData,
      filterGroups,
      columnVisibilityConfig,
      columnManagerPresets,
      setFieldFilter,
    },
    selectionProps: {
      selectedRows,
      clearSelection,
    },
    nameLookupMaps,
    isNameDataLoading,
  };
};
